/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   JobStatusBadge.tsx
 *     Feature         :   114-job-infrastructure
 *     Description     :   Status badge component for job runs
 *     Constitution    :   v1.11.0 | Principle XXXVIII (Specification Testing Standards)
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-28
 * =========================================================================================
 */

import React from 'react';
import {
  CheckCircle2,
  AlertCircle,
  XCircle,
  Clock,
  Hourglass,
  Download,
} from 'lucide-react';

import { Badge, Spinner } from '../../components/ui';
import type { BadgeVariant } from '../../components/ui';
import { JobRunStatus, getStatusLabel, getStatusColor } from '../../services/jobService';

interface JobStatusBadgeProps {
  status: JobRunStatus;
  showLabel?: boolean;
  size?: 'small' | 'medium';
}

/**
 * Get appropriate icon for job status
 */
function getStatusIcon(status: JobRunStatus): React.ReactElement | undefined {
  switch (status) {
    case 'RUNNING':
      return <Spinner size="xs" />;
    case 'COMPLETED':
      return <CheckCircle2 className="h-4 w-4" />;
    case 'FAILED':
      return <AlertCircle className="h-4 w-4" />;
    case 'TIMEOUT':
      return <Clock className="h-4 w-4" />;
    case 'CANCELLED':
      return <XCircle className="h-4 w-4" />;
    case 'PENDING':
      return <Hourglass className="h-4 w-4" />;
    case 'QUEUED':
      return <Clock className="h-4 w-4" />;
    case 'ACR_IMAGE_PULL_FAILED':
      return <Download className="h-4 w-4" />;
    default:
      return undefined;
  }
}

export default function JobStatusBadge({
  status,
  showLabel = true,
  size = 'small',
}: JobStatusBadgeProps) {
  const color = getStatusColor(status);
  const label = getStatusLabel(status);
  const icon = getStatusIcon(status);

  // Map status color → kit Badge variant.
  const variant: BadgeVariant = color;

  return (
    <Badge
      variant={variant}
      size={size === 'medium' ? 'md' : 'sm'}
      isOutlined={color === 'default'}
      leftIcon={icon}
    >
      {showLabel ? label : undefined}
    </Badge>
  );
}
